import { useAppDispatch, useAppSelector } from "../../hooks";
import Button from "./Button";
import { StarIcon } from "@heroicons/react/24/solid";
import CardWithImageTopAndFooter from "./CardWithImageTopAndFooter";
import { CalendarDateRangeIcon, TagIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { getOtherContent } from "../../slices/content";
import Spinner from "./Spinner";
import { Event, People, Tag, Tool } from "../../types/content";
import EventCard from "./EventCard";
import PeopleCard from "./PeopleCard";
import ToolCard from "./ToolCard";
import SupportCard from "./SupportCard";


export default function OtherContent({ showPagination = false, topic, collection }: { showPagination?: boolean, topic: string | undefined, collection: string | undefined }) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getOtherContent({
      pageNumber: 1,
      contentType: "Events",
      topic: topic,
      collection: collection,
    }));
  }, []);

  // Get app level state from redux store
  const { user, loading: authLoading } = useAppSelector((state) => state.auth);
  const {
    events: { data: eventsData, loading: eventsLoading, waiting: eventsWaiting, pagination: eventsPagination },
    peoples: { data: peoplesData, loading: peoplesLoading, waiting: peoplesWaiting, pagination: peoplesPagination },
    tools: { data: toolsData, loading: toolsLoading, waiting: toolsWaiting, pagination: toolsPagination },
    supports: { data: supportData, loading: supportLoading, waiting: supportWaiting, pagination: supportPagination },
  } = useAppSelector((state) => state.content);

  // Component level state
  const [otherContentFilter, setOtherContentFilter] = useState("Events");
  const [pageNumber, setPageNumber] = useState(1);

  return (
    <div
      className="grid grid-cols-1 items-center justify-center mx-auto bg-off-white relative"
    >
      {/* Filters */}
      <div className="flex flex-row flex-wrap gap-x-4 justify-center items-center">
        <span className="font-semibold text-dark-grey text-base">Show:</span>

        {/* Consumption filters */}
        <div className="flex flex-wrap gap-2 justify-center items-center px-8 md:px-10 lg:px-12">

          {/* Events */}
          <Button
            key={`show-other-content-events`}
            onClick={() => {
              setOtherContentFilter("Events");
              setPageNumber(1);
              dispatch(getOtherContent({ pageNumber: pageNumber, contentType: "Events", topic: topic, collection: collection }));
            }}
            width="fit"
            bgColour={otherContentFilter === "Events" ? `dark-grey` : `transparent hover:bg-dark-grey`}
            borderColour="dark-grey"
            textColour={otherContentFilter === "Events" ? `white` : `dark-grey hover:text-white`}
          >
            {otherContentFilter === "Events" && (
              <span className="h-4 p-2 rounded-full bg-white mr-2" />
            )}
            Events
          </Button>

          {/* People */}
          {/* <Button
            key={`show-other-content-people`}
            onClick={() => {
              setOtherContentFilter("People");
              setPageNumber(1);
              dispatch(getOtherContent({ pageNumber: pageNumber, contentType: "People", topic: topic, collection: collection }));
            }}
            width="fit"
            bgColour={otherContentFilter === "People" ? `dark-grey` : `transparent hover:bg-dark-grey`}
            borderColour="dark-grey"
            textColour={otherContentFilter === "People" ? `white` : `dark-grey hover:text-white`}
          >
            {otherContentFilter === "People" && (
              <span className="h-4 p-2 rounded-full bg-white mr-2" />
            )}
            People
          </Button> */}

          {/* Tools */}
          <Button
            key={`show-other-content-tools`}
            onClick={() => {
              setOtherContentFilter("Tools");
              setPageNumber(1);
              dispatch(getOtherContent({ pageNumber: pageNumber, contentType: "Tools", topic: topic, collection: collection }));
            }}
            width="fit"
            bgColour={otherContentFilter === "Tools" ? `dark-grey` : `transparent hover:bg-dark-grey`}
            borderColour="dark-grey"
            textColour={otherContentFilter === "Tools" ? `white` : `dark-grey hover:text-white`}
          >
            {otherContentFilter === "Tools" && (
              <span className="h-4 p-2 rounded-full bg-white mr-2" />
            )}
            Tools
          </Button>

          {/* Support */}
          <Button
            key={`show-other-content-support`}
            onClick={() => {
              setOtherContentFilter("Support");
              setPageNumber(1);
              dispatch(getOtherContent({ pageNumber: pageNumber, contentType: "Support", topic: topic, collection: collection }));
            }}
            width="fit"
            bgColour={otherContentFilter === "Support" ? `dark-grey` : `transparent hover:bg-dark-grey`}
            borderColour="dark-grey"
            textColour={otherContentFilter === "Support" ? `white` : `dark-grey hover:text-white`}
          >
            {otherContentFilter === "Support" && (
              <span className="h-4 p-2 rounded-full bg-white mr-2" />
            )}
            Support
          </Button>

        </div>
      </div>

      {/* Content */}
      {otherContentFilter === "Events" && (
        eventsLoading ? <div className="w-full flex justify-center mx-auto my-20"><Spinner size={8} /></div> : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-16 md:px-20 lg:px-32">
            {eventsData.map((content: Event, index) => {
              return (
                <EventCard
                  key={content.id}
                  objectId={content.id}
                  objectType="Event"
                  image={content.images.length > 0 ? content.images[0] : "/img/graphics/hero/landing-hero.png"}
                  bgColour="dark-beige"
                  href={content.url}
                  title={content.title}
                  host={content.host}
                  city={content?.city?.name}
                  cityDetails={content?.city_details}
                  startDate={content.start_date}
                  endDate={content.end_date}
                  synopsis={content.synopsis}
                  primaryTags={content.tags}
                  user={user}
                  rating={content.rating}
                  reviews={content.reviews}
                  price={content.price}
                />
              )
            })}
          </div>
        )
      )}

      {otherContentFilter === "People" && (
        peoplesLoading ? <div className="w-full flex justify-center mx-auto my-20"><Spinner size={8} /></div> : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-8 md:px-12 lg:px-16">
            {peoplesData.map((content: People, index) => {
              return (
                <PeopleCard
                  key={content.id}
                  objectId={content.id}
                  objectType="People"
                  image={content.images.length > 0 ? content.images[0] : "/img/graphics/hero/landing-hero.png"}
                  bgColour="dark-beige"
                  href={content.url}
                  name={content.name}
                  profession={content.profession}
                  synopsis={content.synopsis}
                  primaryTags={content.tags}
                  user={user}
                  rating={content.rating}
                  reviews={content.reviews}
                />
              )
            })}
          </div>
        )
      )}

      {otherContentFilter === "Tools" && (
        toolsLoading ? <div className="w-full flex justify-center mx-auto my-20"><Spinner size={8} /></div> : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-8 md:px-12 lg:px-16">
            {toolsData.map((content: Tool, index) => {
              return (
                <ToolCard
                  key={content.id}
                  objectId={content.id}
                  objectType="Tool"
                  image={content.images.length > 0 ? content.images[0] : "/img/graphics/hero/landing-hero.png"}
                  bgColour="dark-beige"
                  href={content.url}
                  name={content.name}
                  synopsis={content.synopsis}
                  primaryTags={content.tags}
                  user={user}
                  rating={content.rating}
                  reviews={content.reviews}
                  price={content.price}
                />
              )
            })}
          </div>
        )
      )}

      {otherContentFilter === "Support" && (
        supportLoading ? <div className="w-full flex justify-center mx-auto my-20"><Spinner size={8} /></div> : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-8 md:px-12 lg:px-16">
            {supportData.map((content: any, index) => {
              return (
                <SupportCard
                  key={content.id}
                  objectId={content.id}
                  image={content.images.length > 0 ? content.images[0] : "/img/graphics/hero/landing-hero.png"}
                  bgColour="dark-beige"
                  href={content.url}
                  name={content.name}
                  synopsis={content.synopsis}
                  primaryTags={content.tags}
                  user={user}
                  rating={content.rating}
                  reviews={content.reviews}
                  price={content.price}
                />
              )
            })}
          </div>
        )
      )}

      {/* Pagination */}
      {otherContentFilter === "Events" && showPagination && eventsPagination && eventsPagination.has_next && (
        <div className="mt-8 flex items-center justify-center">
          {eventsWaiting ? <Spinner size={6} /> : (
            <Button
              onClick={() => {
                setPageNumber(pageNumber + 1);
                dispatch(getOtherContent({ pageNumber: pageNumber + 1, contentType: "Events", topic: topic, collection: collection }));
              }}
              width="fit"
              bgColour="transparent hover:bg-dark-grey"
              borderColour="dark-grey"
              textColour="dark-grey hover:text-white"
            >
              Show more
            </Button>
          )}
        </div>
      )}

      {otherContentFilter === "People" && showPagination && peoplesPagination && peoplesPagination.has_next && (
        <div className="mt-8 flex items-center justify-center">
          {peoplesWaiting ? <Spinner size={6} /> : (
            <Button
              onClick={() => {
                setPageNumber(pageNumber + 1);
                dispatch(getOtherContent({ pageNumber: pageNumber + 1, contentType: "People", topic: topic, collection: collection }));
              }}
              width="fit"
              bgColour="transparent hover:bg-dark-grey"
              borderColour="dark-grey"
              textColour="dark-grey hover:text-white"
            >
              Show more
            </Button>
          )}
        </div>
      )}

      {otherContentFilter === "Tools" && showPagination && toolsPagination && toolsPagination.has_next && (
        <div className="mt-8 flex items-center justify-center">
          {toolsWaiting ? <Spinner size={6} /> : (
            <Button
              onClick={() => {
                setPageNumber(pageNumber + 1);
                dispatch(getOtherContent({ pageNumber: pageNumber + 1, contentType: "Tools", topic: topic, collection: collection }));
              }}
              width="fit"
              bgColour="transparent hover:bg-dark-grey"
              borderColour="dark-grey"
              textColour="dark-grey hover:text-white"
            >
              Show more
            </Button>
          )}
        </div>
      )}

      {otherContentFilter === "Support" && showPagination && supportPagination && supportPagination.has_next && (
        <div className="mt-8 flex items-center justify-center">
          {supportWaiting ? <Spinner size={6} /> : (
            <Button
              onClick={() => {
                setPageNumber(pageNumber + 1);
                dispatch(getOtherContent({ pageNumber: pageNumber + 1, contentType: "Support", topic: topic, collection: collection }));
              }}
              width="fit"
              bgColour="transparent hover:bg-dark-grey"
              borderColour="dark-grey"
              textColour="dark-grey hover:text-white"
            >
              Show more
            </Button>
          )}
        </div>
      )}

    </div>
  );
}
